import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Edit, Download, X, ChevronUp, ChevronDown, Search } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { usePermissions } from '../hooks/usePermissions';
import { compareIssues } from '../utils/issueUtils';

const Artwork = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isViewOnly } = usePermissions();
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [initialIssueSet, setInitialIssueSet] = useState(false);
  const [magazines, setMagazines] = useState([]);
  const [contentSizes, setContentSizes] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [availableIssues, setAvailableIssues] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const latestArtworkRequestRef = useRef(0);

  const getCurrentIssueFromBackend = async () => {
    try {
      const response = await api.get('/bookings/current-issue');
      return response.data.currentIssue;
    } catch (error) {
      console.error('Error getting current issue:', error);
      return null;
    }
  };

  const getCurrentIssue = () => {
    if (availableIssues.length === 0 || schedules.length === 0) {
      return null;
    }

    const now = new Date();

    const issuesWithDates = [];
    schedules.forEach(schedule => {
      if (schedule.issues && Array.isArray(schedule.issues)) {
        schedule.issues.forEach(issue => {
          issuesWithDates.push({
            name: issue.name,
            closeDate: new Date(issue.closeDate),
          });
        });
      }
    });

    for (const issue of availableIssues) {
      const issueData = issuesWithDates.find(i => i.name === issue);
      if (issueData && issueData.closeDate >= now) {
        return issue;
      }
    }

    return availableIssues[availableIssues.length - 1];
  };

  const isIssueClosed = issueName => {
    const currentDate = new Date();

    for (const schedule of schedules) {
      const scheduleIssue = schedule.issues?.find(issue => issue.name === issueName);
      if (scheduleIssue && new Date(scheduleIssue.closeDate) < currentDate) {
        return true;
      }
    }

    return false;
  };

  const [filters, setFilters] = useState({
    issue: searchParams.get('issue') || '',
    searchTerm: searchParams.get('search') || '',
    magazine: searchParams.get('magazine') || '',
    size: searchParams.get('size') || '',
    type: searchParams.get('type') || '',
    hasNotes: searchParams.get('hasNotes') || 'all',
  });

  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (initialIssueSet || filters.issue) {
      loadArtworkEntries();
    }
  }, [filters, initialIssueSet]);

  useEffect(() => {
    const setInitialIssue = async () => {
      if (availableIssues.length > 0 && !initialIssueSet) {
        const urlIssue = searchParams.get('issue');

        if (urlIssue && availableIssues.includes(urlIssue)) {
          if (filters.issue !== urlIssue) {
            setFilters(prev => ({ ...prev, issue: urlIssue }));
          }
          setInitialIssueSet(true);
          return;
        }

        const backendCurrentIssue = await getCurrentIssueFromBackend();
        if (
          backendCurrentIssue &&
          availableIssues.includes(backendCurrentIssue) &&
          !isIssueClosed(backendCurrentIssue)
        ) {
          setFilters(prev => ({ ...prev, issue: backendCurrentIssue }));
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('issue', backendCurrentIssue);
          setSearchParams(newSearchParams, { replace: true });
          setInitialIssueSet(true);
          return;
        }

        const currentIssue = getCurrentIssue();
        if (currentIssue && availableIssues.includes(currentIssue)) {
          setFilters(prev => ({ ...prev, issue: currentIssue }));
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('issue', currentIssue);
          setSearchParams(newSearchParams, { replace: true });
          setInitialIssueSet(true);
        } else if (availableIssues.length > 0) {
          const firstIssue = availableIssues[0];
          setFilters(prev => ({ ...prev, issue: firstIssue }));
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('issue', firstIssue);
          setSearchParams(newSearchParams, { replace: true });
          setInitialIssueSet(true);
        }
      }
    };

    setInitialIssue();
  }, [availableIssues, initialIssueSet]);

  const loadSchedules = async () => {
    try {
      const response = await api.get('/schedules');
      setSchedules(response.data);
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadMagazines(),
        loadContentSizes(),
        loadContentTypes(),
        loadSchedules(),
      ]);
      await loadArtworkEntries();
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load artwork data');
    } finally {
      setLoading(false);
    }
  };

  const loadArtworkEntries = async () => {
    const requestId = latestArtworkRequestRef.current + 1;
    latestArtworkRequestRef.current = requestId;

    try {
      setFilterLoading(true);

      const queryParams = new URLSearchParams();

      if (filters.issue) queryParams.append('issue', filters.issue);
      if (filters.searchTerm) queryParams.append('search', filters.searchTerm);
      if (filters.magazine) queryParams.append('magazine', filters.magazine);
      if (filters.size) queryParams.append('size', filters.size);
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.hasNotes && filters.hasNotes !== 'all') {
        queryParams.append('hasNotes', filters.hasNotes);
      }

      const response = await api.get(`/bookings/artwork-entries?${queryParams.toString()}`);
      const data = response.data;
      let entries = data.entries || [];

      if (filters.searchTerm) {
        const term = filters.searchTerm.trim().toLowerCase();
        entries = entries.filter(entry => entry.customer?.name?.toLowerCase().includes(term));
      }

      if (requestId !== latestArtworkRequestRef.current) {
        return;
      }

      setFilteredEntries(entries);

      const sortedIssues = (data.availableIssues || []).sort(compareIssues);
      setAvailableIssues(sortedIssues);
    } catch (error) {
      if (requestId === latestArtworkRequestRef.current) {
        await loadArtworkEntriesClientSide(requestId);
      }
    } finally {
      if (requestId === latestArtworkRequestRef.current) {
        setFilterLoading(false);
      }
    }
  };

  const loadArtworkEntriesClientSide = async requestId => {
    try {
      const response = await api.get('/bookings');
      // The flat list endpoint returns { entries: [...] }; older payload shapes
      // returned a raw array.
      const rawBookings = response.data.entries || response.data.data || response.data;
      const bookings = Array.isArray(rawBookings) ? rawBookings : [];

      const entries = [];
      const issuesSet = new Set();

      bookings.forEach(booking => {
        (booking.monthlyArtwork || [])
          .filter(artwork => artwork.isSelected)
          .forEach(artwork => {
            entries.push({
              id: `${booking._id || booking.bookingId}-${artwork.month}`,
              bookingId: booking._id || booking.bookingId,
              customer: booking.customer,
              magazine: booking.magazine,
              contentSize: booking.contentSize,
              contentType: booking.contentType,
              issue: artwork.month,
              artworkNote: artwork.artworkNote || '',
              filename: artwork.filename || '',
              createdAt: booking.createdAt,
            });
            issuesSet.add(artwork.month);
          });
      });

      let filtered = [...entries];

      if (filters.issue) {
        filtered = filtered.filter(entry => entry.issue === filters.issue);
      }

      if (filters.searchTerm) {
        const term = filters.searchTerm.trim().toLowerCase();
        filtered = filtered.filter(entry => entry.customer?.name?.toLowerCase().includes(term));
      }

      if (filters.magazine) {
        filtered = filtered.filter(
          entry => entry.magazine?._id === filters.magazine || entry.magazine === filters.magazine
        );
      }

      if (filters.size) {
        filtered = filtered.filter(
          entry => entry.contentSize?._id === filters.size || entry.contentSize === filters.size
        );
      }

      if (filters.type) {
        filtered = filtered.filter(entry => entry.contentType === filters.type);
      }

      if (filters.hasNotes === 'has') {
        filtered = filtered.filter(
          entry =>
            (entry.artworkNote && entry.artworkNote.trim()) ||
            (entry.filename && entry.filename.trim())
        );
      } else if (filters.hasNotes === 'no') {
        filtered = filtered.filter(
          entry =>
            (!entry.artworkNote || !entry.artworkNote.trim()) &&
            (!entry.filename || !entry.filename.trim())
        );
      }

      if (requestId && requestId !== latestArtworkRequestRef.current) {
        return;
      }

      setFilteredEntries(filtered);

      const sortedIssues = Array.from(issuesSet).sort(compareIssues);
      setAvailableIssues(sortedIssues);
    } catch (error) {
      console.error('Error loading artwork entries:', error);
      throw error;
    }
  };

  const loadMagazines = async () => {
    try {
      const response = await api.get('/magazines');
      setMagazines(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading publication:', error);
    }
  };

  const loadContentSizes = async () => {
    try {
      const response = await api.get('/content-sizes');
      setContentSizes(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading content sizes:', error);
    }
  };

  const loadContentTypes = async () => {
    try {
      const response = await api.get('/content-types');
      setContentTypes(response.data || []);
    } catch (error) {
      console.error('Error loading content types:', error);
    }
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value,
    };
    setFilters(newFilters);

    const newSearchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, val]) => {
      if (val && val !== 'all') {
        const paramName = key === 'searchTerm' ? 'search' : key;
        newSearchParams.set(paramName, val);
      }
    });
    setSearchParams(newSearchParams);
  };

  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = field => {
    if (sortField !== field) {
      return <div className="w-4 h-4"></div>;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  const sortedEntries = useMemo(() => {
    if (!sortField) return filteredEntries;

    const sorted = [...filteredEntries].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'customer':
          aValue = a.customer?.name || '';
          bValue = b.customer?.name || '';
          break;
        case 'businessType':
          aValue =
            a.customer?.businessTypes && a.customer.businessTypes.length > 0
              ? a.customer.businessTypes.map(bt => bt.section).join(', ')
              : '';
          bValue =
            b.customer?.businessTypes && b.customer.businessTypes.length > 0
              ? b.customer.businessTypes.map(bt => bt.section).join(', ')
              : '';
          break;
        case 'magazine':
          aValue = a.magazine?.name || '';
          bValue = b.magazine?.name || '';
          break;
        case 'size':
          aValue = a.contentSize?.adSize?.name || '';
          bValue = b.contentSize?.adSize?.name || '';
          break;
        case 'type':
          aValue = a.contentType || '';
          bValue = b.contentType || '';
          break;
        case 'issue':
          aValue = a.issue || '';
          bValue = b.issue || '';
          if (aValue && bValue) {
            return sortDirection === 'asc'
              ? compareIssues(aValue, bValue)
              : compareIssues(bValue, aValue);
          }
          break;
        default:
          return 0;
      }

      if (sortField !== 'issue') {
        const comparison = aValue.toString().localeCompare(bValue.toString(), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

    return sorted;
  }, [filteredEntries, sortField, sortDirection]);

  const clearFilters = async () => {
    let defaultIssue = '';

    const backendCurrentIssue = await getCurrentIssueFromBackend();
    if (backendCurrentIssue && availableIssues.includes(backendCurrentIssue)) {
      defaultIssue = backendCurrentIssue;
    } else {
      // Fallback to client-side logic
      const currentIssue = getCurrentIssue();
      if (currentIssue && availableIssues.includes(currentIssue)) {
        defaultIssue = currentIssue;
      } else if (availableIssues.length > 0) {
        defaultIssue = availableIssues[0];
      }
    }

    const clearedFilters = {
      issue: defaultIssue,
      searchTerm: '',
      magazine: '',
      size: '',
      type: '',
      hasNotes: 'all',
    };
    setFilters(clearedFilters);

    const newSearchParams = new URLSearchParams();
    if (defaultIssue) {
      newSearchParams.set('issue', defaultIssue);
    }
    setSearchParams(newSearchParams);
  };

  const handleEdit = entry => {
    const customerId = entry.customer?._id || entry.customer;
    if (customerId) {
      navigate(`/bookings/customer/${customerId}`);
    } else {
      navigate('/bookings');
    }
  };

  const handleExport = () => {
    const headers = [
      'Customer',
      'Business Type',
      'Publication',
      'Size',
      'Type',
      'Issue',
      'Artwork Notes',
      'Artwork File Name',
    ];
    const csvContent = [
      headers.join(','),
      ...filteredEntries.map(entry =>
        [
          `"${entry.customer?.name || 'N/A'}"`,
          `"${
            entry.customer?.businessTypes && entry.customer.businessTypes.length > 0
              ? entry.customer.businessTypes.map(bt => bt.section).join(', ')
              : 'N/A'
          }"`,
          `"${entry.magazine?.name || 'N/A'}"`,
          `"${entry.contentSize?.adSize?.name || 'N/A'}"`,
          `"${entry.contentType || 'N/A'}"`,
          `"${entry.issue || 'N/A'}"`,
          `"${entry.artworkNote || ''}"`,
          `"${entry.filename || ''}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `artwork-entries-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success('Artwork entries exported successfully');
  };

  const handleCreateIndex = async () => {
    if (!filters.magazine) {
      toast.error('Please select a publication first', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }

    if (!filters.issue) {
      toast.error('Please select an issue', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }

    try {
      const toastId = toast.loading('Generating advertiser index...', {
        position: 'top-center',
      });

      const response = await api.get('/advertiser-index/csv', {
        params: {
          magazineId: filters.magazine,
          issueName: filters.issue,
        },
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'advertiser-index.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.update(toastId, {
        render: 'Advertiser index created successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
        position: 'top-center',
      });
    } catch (error) {
      console.error('Error creating advertiser index:', error);

      let errorMessage = 'Failed to create advertiser index';

      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const jsonError = JSON.parse(text);
            errorMessage = jsonError.message || errorMessage;
          } catch (e) {
            console.error('Could not parse error blob:', e);
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }

      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 5000,
      });
    }
  };

  const truncateText = (text, maxLength = 15) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <Layout>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-12 text-center">
            <div className="inline-block w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
            <p className="mt-2 text-gray-600">Loading artwork entries...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Artwork Management</h1>
              <p className="mt-2 text-gray-600">
                Manage artwork notes and files for graphic design
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleCreateIndex}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  !filters.magazine
                    ? 'bg-green-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                title={
                  !filters.magazine
                    ? 'Please select a publication first'
                    : 'Create advertiser index'
                }
              >
                <Download className="mr-2 w-4 h-4" />
                Create Index
              </button>
              <button
                onClick={handleExport}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700"
              >
                <Download className="mr-2 w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
            <div className="mb-4 w-full">
                <div className="relative max-w-md">
                  <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                    <Search className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={filters.searchTerm}
                    onChange={e => handleFilterChange('searchTerm', e.target.value)}
                    className="block py-2 pr-3 pl-10 w-full text-sm leading-5 placeholder-gray-500 bg-white rounded-md border border-gray-300 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search Customers"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Issue</label>
                  <select
                    value={filters.issue}
                    onChange={e => handleFilterChange('issue', e.target.value)}
                    className="block px-3 py-2 w-full text-sm rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {availableIssues.map(issue => {
                      const currentIssue = getCurrentIssue();
                      const isCurrent = currentIssue && issue === currentIssue;
                      const isClosed = isIssueClosed(issue);
                      const labels = [];
                      if (isCurrent) labels.push('Current');
                      if (isClosed) labels.push('Closed');

                      return (
                        <option
                          key={issue}
                          value={issue}
                          style={{ color: isClosed ? '#9ca3af' : 'inherit' }}
                        >
                          {labels.length > 0 ? `${issue} (${labels.join(', ')})` : issue}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Publication
                  </label>
                  <select
                    value={filters.magazine}
                    onChange={e => handleFilterChange('magazine', e.target.value)}
                    className="block px-3 py-2 w-full text-sm rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Publications</option>
                    {magazines.map(magazine => (
                      <option key={magazine._id} value={magazine._id}>
                        {magazine.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Size</label>
                  <select
                    value={filters.size}
                    onChange={e => handleFilterChange('size', e.target.value)}
                    className="block px-3 py-2 w-full text-sm rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Sizes</option>
                    {contentSizes.map(size => (
                      <option key={size._id} value={size._id}>
                        {size.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={filters.type}
                    onChange={e => handleFilterChange('type', e.target.value)}
                    className="block px-3 py-2 w-full text-sm rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Types</option>
                    {contentTypes.map(type => (
                      <option key={type._id || type.name} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Has Notes?</label>
                  <select
                    value={filters.hasNotes}
                    onChange={e => handleFilterChange('hasNotes', e.target.value)}
                    className="block px-3 py-2 w-full text-sm rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="has">Has notes</option>
                    <option value="no">No notes</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="flex px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50"
                  >
                    <X className="mr-1 w-4 h-4" />
                    Clear Filter
                  </button>
                </div>
              </div>
            </div>
          </div>

        {/* Artwork Entries Table */}
        <div className="overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('customer')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer</span>
                      {getSortIcon('customer')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('businessType')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Business Type</span>
                      {getSortIcon('businessType')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('magazine')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Publication</span>
                      {getSortIcon('magazine')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('size')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Size</span>
                      {getSortIcon('size')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Type</span>
                      {getSortIcon('type')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('issue')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Issue</span>
                      {getSortIcon('issue')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase">
                    Artwork Notes
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase">
                    Artwork File Name
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filterLoading ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                      <div className="inline-block mr-2 w-4 h-4 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                      Loading...
                    </td>
                  </tr>
                ) : sortedEntries.length > 0 ? (
                  sortedEntries.map(entry => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {entry.customer?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {entry.customer?.businessTypes && entry.customer.businessTypes.length > 0
                            ? entry.customer.businessTypes.map(bt => bt.section).join(', ')
                            : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{entry.magazine?.name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {entry.contentSize?.adSize?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{entry.contentType || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{entry.issue || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900" title={entry.artworkNote}>
                          {truncateText(entry.artworkNote)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900" title={entry.filename}>
                          {truncateText(entry.filename)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                        {!isViewOnly() && (
                          <button
                            onClick={() => handleEdit(entry)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit Booking"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                      No artwork entries found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Artwork;
